/**
 * AddParadaExtraDialog — Iter 87 (Feb 2026)
 *
 * Diálogo para añadir una parada manual a una ruta existente (en curso o no).
 * Soporta:
 *   - Selección de cliente del CRM (con autocomplete)
 *   - Selección de espacio del cliente (auto-rellena dirección y geo)
 *   - Importe a facturar + concepto para FD
 *   - Si no se selecciona cliente/espacio, se puede meter dirección libre
 *     y el backend geocodifica
 */
import React, { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, X } from "lucide-react";

export default function AddParadaExtraDialog({ open, onOpenChange, rutaId, onAdded, editPin }) {
  const [titulo, setTitulo] = useState("");
  const [clienteQ, setClienteQ] = useState("");
  const [clientes, setClientes] = useState([]);
  const [clienteSel, setClienteSel] = useState(null);
  const [espacios, setEspacios] = useState([]);
  const [espacioSel, setEspacioSel] = useState(null);
  const [direccion, setDireccion] = useState("");
  const [ciudad, setCiudad] = useState("");
  const [provincia, setProvincia] = useState("");
  const [importe, setImporte] = useState("");
  const [concepto, setConcepto] = useState("");
  const [contacto, setContacto] = useState("");
  const [telefono, setTelefono] = useState("");
  const [duracion, setDuracion] = useState("");
  const [observaciones, setObservaciones] = useState("");
  const [horaEst, setHoraEst] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset al abrir
  useEffect(() => {
    if (open) {
      setTitulo("");
      setClienteQ("");
      setClienteSel(null);
      setEspacios([]);
      setEspacioSel(null);
      setDireccion("");
      setCiudad("");
      setProvincia("");
      setImporte("");
      setConcepto("");
      setContacto("");
      setTelefono("");
      setDuracion("");
      setObservaciones("");
      setHoraEst("");
    }
  }, [open]);

  // Buscar clientes con debounce simple
  useEffect(() => {
    if (!open) return;
    if (!clienteQ || clienteQ.length < 2) { setClientes([]); return; }
    const h = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clientes?q=${encodeURIComponent(clienteQ)}`);
        setClientes((data || []).slice(0, 10));
      } catch {
        setClientes([]);
      }
    }, 300);
    return () => clearTimeout(h);
  }, [clienteQ, open]);

  // Cargar espacios del cliente seleccionado
  useEffect(() => {
    if (!clienteSel) { setEspacios([]); return; }
    api.get(`/espacios?cliente_id=${clienteSel.id}`)
      .then(({ data }) => setEspacios(data || []))
      .catch(() => setEspacios([]));
  }, [clienteSel]);

  // Cuando seleccionas espacio, autorellena
  useEffect(() => {
    if (!espacioSel) return;
    setDireccion(espacioSel.direccion || "");
    setCiudad(espacioSel.ciudad || "");
    setProvincia(espacioSel.provincia || "");
  }, [espacioSel]);

  const canSave = useMemo(() => !!titulo.trim() && !saving, [titulo, saving]);

  const guardar = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await api.post(`/rutas/${rutaId}/paradas`, {
        titulo: titulo.trim(),
        direccion: direccion.trim() || null,
        ciudad: ciudad.trim() || null,
        provincia: provincia.trim() || null,
        contacto: contacto.trim() || null,
        telefono: telefono.trim() || null,
        duracion_min: duracion ? parseInt(duracion, 10) : null,
        observaciones: observaciones.trim() || null,
        cliente_id: clienteSel?.id || null,
        espacio_id: espacioSel?.id || null,
        importe: importe ? parseFloat(importe) : null,
        concepto: concepto.trim() || null,
        hora_estimada: horaEst.trim() || null,
      }, { headers: { "X-Ruta-Edit-Pin": editPin } });
      toast.success("Parada añadida");
      onAdded?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error añadiendo parada");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-amber-500/40 max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="dialog-add-parada-extra">
        <DialogHeader>
          <DialogTitle className="text-amber-200">＋ Añadir parada extra a la ruta</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <Label className="text-xs text-zinc-400">Título * <span className="text-zinc-600">(ej: "Cambio rótulo fachada Express")</span></Label>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)}
              className="bg-zinc-900 border-zinc-800 mt-1"
              data-testid="parada-extra-titulo" maxLength={120} />
          </div>

          {/* Selector de cliente del CRM */}
          <div>
            <Label className="text-xs text-zinc-400">Cliente del CRM <span className="text-zinc-600">(opcional)</span></Label>
            {clienteSel ? (
              <div className="flex items-center justify-between gap-2 mt-1 px-3 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded">
                <span className="text-sm text-emerald-200">{clienteSel.nombre || clienteSel.razon_social}</span>
                <button type="button" onClick={() => { setClienteSel(null); setEspacioSel(null); setClienteQ(""); }}
                  className="text-zinc-400 hover:text-red-400" data-testid="parada-extra-cliente-clear">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="relative mt-1">
                <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
                <Input value={clienteQ} onChange={(e) => setClienteQ(e.target.value)}
                  placeholder="Buscar cliente…"
                  className="bg-zinc-900 border-zinc-800 pl-8"
                  data-testid="parada-extra-cliente-q" />
                {clientes.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded shadow-lg max-h-48 overflow-y-auto z-10">
                    {clientes.map((c) => (
                      <button key={c.id} type="button" onClick={() => setClienteSel(c)}
                        data-testid={`parada-extra-cliente-${c.id}`}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-zinc-800 text-zinc-200">
                        {c.nombre || c.razon_social}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Espacios del cliente */}
          {clienteSel && espacios.length > 0 && (
            <div>
              <Label className="text-xs text-zinc-400">Espacio (tienda/PDV) <span className="text-zinc-600">(opcional, autorrellena dirección)</span></Label>
              <select value={espacioSel?.id || ""}
                onChange={(e) => setEspacioSel(espacios.find((x) => x.id === e.target.value) || null)}
                data-testid="parada-extra-espacio"
                className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-200">
                <option value="">— Sin espacio (introducir dirección libre) —</option>
                {espacios.map((es) => (
                  <option key={es.id} value={es.id}>
                    {es.nombre} {es.ciudad ? `· ${es.ciudad}` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Dirección */}
          <div>
            <Label className="text-xs text-zinc-400">Dirección</Label>
            <Input value={direccion} onChange={(e) => setDireccion(e.target.value)}
              placeholder="Calle, número"
              className="bg-zinc-900 border-zinc-800 mt-1"
              data-testid="parada-extra-direccion" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Ciudad</Label>
              <Input value={ciudad} onChange={(e) => setCiudad(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="parada-extra-ciudad" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Provincia</Label>
              <Input value={provincia} onChange={(e) => setProvincia(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="parada-extra-provincia" />
            </div>
          </div>

          {/* Importe + concepto */}
          <div className="bg-amber-500/5 border border-amber-500/30 rounded p-3">
            <div className="text-[10px] uppercase tracking-wider text-amber-300 mb-1.5">Facturación (opcional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Importe (€)</Label>
                <Input type="number" min="0" step="0.01" value={importe}
                  onChange={(e) => setImporte(e.target.value)}
                  placeholder="0.00"
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="parada-extra-importe" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Concepto (línea albarán)</Label>
                <Input value={concepto} onChange={(e) => setConcepto(e.target.value)}
                  placeholder="Cambio rótulo Express"
                  className="bg-zinc-900 border-zinc-800 mt-1"
                  data-testid="parada-extra-concepto" />
              </div>
            </div>
            <div className="text-[10px] text-zinc-500 mt-2">
              Si el cliente tiene albarán abierto del mes, se sumará. Si no, se creará uno nuevo al cerrar el reporte.
            </div>
          </div>

          {/* Contacto y telefono y hora */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Contacto</Label>
              <Input value={contacto} onChange={(e) => setContacto(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="parada-extra-contacto" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Teléfono</Label>
              <Input type="tel" value={telefono} onChange={(e) => setTelefono(e.target.value)}
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="parada-extra-telefono" />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Hora estimada</Label>
              <Input value={horaEst} onChange={(e) => setHoraEst(e.target.value)}
                placeholder="14:30"
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="parada-extra-hora" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-zinc-400">Duración estimada (min)</Label>
              <Input type="number" min="0" value={duracion}
                onChange={(e) => setDuracion(e.target.value)}
                placeholder="60"
                className="bg-zinc-900 border-zinc-800 mt-1"
                data-testid="parada-extra-duracion" />
            </div>
          </div>

          <div>
            <Label className="text-xs text-zinc-400">Observaciones</Label>
            <Input value={observaciones} onChange={(e) => setObservaciones(e.target.value)}
              placeholder="Indicaciones específicas, herramientas…"
              className="bg-zinc-900 border-zinc-800 mt-1"
              data-testid="parada-extra-obs" maxLength={500} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}
            data-testid="parada-extra-cancel">Cancelar</Button>
          <Button onClick={guardar} disabled={!canSave}
            data-testid="parada-extra-save"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-semibold">
            {saving ? "Guardando…" : "Añadir parada"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
